<script setup>
import {ref, computed, watch, nextTick, onMounted} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import AvatarImage from '@/components/images/AvatarImage.vue';
import UserCard from '~/components/users/UserCard.vue';
import MessageBubble from './MessageBubble.vue';
import MessageInput from './MessageInput.vue';
import TypingIndicator from './TypingIndicator.vue';
import ParticipantsDialog from './ParticipantsDialog.vue';
import {useSafety, apiMessage} from '~/composables/useSafety';
import {blockConfirmation} from '~/composables/useSafetyCopy.js';
import {messageDayLabel, startsNewDay} from '~/composables/useMessageTime.js';
import {useDeviceLocale} from '~/composables/useDeviceLocale';
import {useDates} from '~/composables/useDates';

const props = defineProps({
  conversation: {
    type: Object,
    default: null
  },
  messages: {
    type: Array,
    default: () => []
  },
  typingUsers: {
    type: Set,
    default: () => new Set()
  }
});

const emit = defineEmits(['send', 'send-image', 'retry', 'delete', 'delete-for-everyone', 'typing', 'back', 'update-participants', 'update-admins', 'update-title', 'leave', 'update-notifications']);

const {user} = useAuthService();

// ── Report / block (Online Safety Act) ──
const {blockedUserIds, isBlocked, block, unblock, loadBlocked} = useSafety();
const {setAlertMessage} = useAlertService();
const blockBusy = ref(false);
// Re-read on every open: a block or unblock made elsewhere (the app, another
// tab, the Blocked members list) must never leave a stale Block/Unblock here
// (owner finding, 24 Sep 2026).
onMounted(() => { loadBlocked({force: true}); });
watch(() => props.conversation?.id, (id, was) => { if (id && id !== was) loadBlocked({force: true}); });
// A direct message has exactly one other person; that is who "Block" means here.
const otherBlocked = computed(() => !!otherParticipant.value && isBlocked(otherParticipant.value.id));
const otherName = computed(() => otherParticipant.value
    ? `${otherParticipant.value.firstName || ''} ${otherParticipant.value.lastName || ''}`.trim() : '');
// Blocking is said first and confirmed; unblocking only gives back.
const confirmingBlock = ref(false);
const blockCopy = computed(() => blockConfirmation(otherName.value));
const toggleBlockOther = async () => {
  const id = otherParticipant.value?.id;
  if (!id || blockBusy.value) return;
  if (!otherBlocked.value) {
    confirmingBlock.value = true;
    return;
  }
  blockBusy.value = true;
  try {
    await unblock(id);
    setAlertMessage(`${otherName.value} unblocked. Their messages will show again.`);
  } catch (e) {
    setAlertMessage(apiMessage(e, "Couldn't do that. Please try again."));
  } finally {
    blockBusy.value = false;
  }
};
const confirmBlockOther = async () => {
  const id = otherParticipant.value?.id;
  if (!id) return;
  blockBusy.value = true;
  try {
    await block(id);
    confirmingBlock.value = false;
    setAlertMessage(`${otherName.value} blocked. Neither of you can message the other.`);
  } catch (e) {
    setAlertMessage(apiMessage(e, "Couldn't do that. Please try again."));
  } finally {
    blockBusy.value = false;
  }
};
const {getConversationNotifications} = useMessaging();
const messagesContainer = ref(null);
const showParticipantsDialog = ref(false);
const showEditTitleDialog = ref(false);
const editFormData = ref({title: ''});
const editFormPending = ref(false);
const editFormErrors = ref({});
const showNotificationsDialog = ref(false);
const notificationsFormData = ref({notificationsEnabled: true});
const notificationsFormPending = ref(false);
const notificationsFormErrors = ref({});
const cachedNotificationStatus = ref(null); // Cache notification status (true/false/null)

// Get the other participant for one-to-one chats
const otherParticipant = computed(() => {
  if (props.conversation?.conversationType !== 1) return null;
  if (!props.conversation?.participants) return null;
  return props.conversation.participants.find(p => p.id !== user.value?.id);
});

// Conversation title
const conversationTitle = computed(() => {
  if (props.conversation?.title) {
    return props.conversation.title;
  }

  // For one-to-one chats, show other participant's name
  if (props.conversation?.conversationType === 1 && otherParticipant.value) {
    return `${otherParticipant.value.firstName} ${otherParticipant.value.lastName}`;
  }

  // Fallback
  return 'Conversation';
});

// Participant count (only for group conversations)
const participantCount = computed(() => {
  return props.conversation?.participants?.length || props.conversation?.participantCount || 0;
});

// Show participant count only for group conversations
const showParticipantCount = computed(() => {
  return props.conversation?.conversationType !== 1;
});

// Conversation initials/fallback for avatar
const conversationInitials = computed(() => {
  if (props.conversation?.conversationType === 1 && otherParticipant.value) {
    const first = otherParticipant.value.firstName?.charAt(0) || '';
    const last = otherParticipant.value.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  }

  const title = conversationTitle.value;
  if (!title) return '?';

  const words = title.split(' ');
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
});

// Avatar filename for one-to-one chats
const avatarFilename = computed(() => {
  if (props.conversation?.conversationType === 1 && otherParticipant.value) {
    return otherParticipant.value.imageFileName;
  }
  return null;
});

// Check if current user is still a participant in this conversation
const isCurrentUserParticipant = computed(() => {
  if (!props.conversation || !user.value?.id) return true;
  return props.conversation.participantUserIds?.includes(user.value.id) ?? true;
});

// Check if user has left/been removed from conversation
const hasLeftConversation = computed(() => {
  return props.conversation && !isCurrentUserParticipant.value;
});

// Check if current user can edit participants (is admin for group chats)
const canEditParticipants = computed(() => {
  if (!props.conversation || !user.value) return false;
  // Only group conversations (type 2+) can have participants edited
  if (props.conversation.conversationType === 1) return false;
  // User must be an admin or the creator
  const isCreator = props.conversation.createdByUserId === user.value.id;
  const isAdmin = props.conversation.adminUserIds?.includes(user.value.id);
  return isCreator || isAdmin;
});

// Always show conversation menu (for both single and group conversations)
const showConversationMenu = computed(() => {
  return true; // Show menu for all conversations
});

// Convert typing users Set to array for template
const typingUsersArray = computed(() => {
  return Array.from(props.typingUsers || []);
});

// Render window: long threads (500+) rendered every bubble into the DOM at once.
// Show the most recent chunk and let the user expand backwards on demand.
const RENDER_WINDOW = 200;
const renderLimit = ref(RENDER_WINDOW);
const visibleMessages = computed(() => {
  // Hide anyone the viewer has blocked. The server stops sending their
  // messages on the next sync, but the local cache still holds what arrived
  // before the block, and a block that only took effect after a resync would
  // read as "it didn't work".
  const blocked = blockedUserIds.value;
  const shown = blocked.length === 0
      ? props.messages
      : props.messages.filter(m => !blocked.includes(m.senderUserId));
  if (shown.length <= renderLimit.value) return shown;
  return shown.slice(-renderLimit.value);
});
// A divider above the first message of each day ("Today", "Yesterday",
// "Thu 1 Oct"), as the app's thread draws one (_DayDivider).
const deviceLocale = useDeviceLocale();
const dayLabelFor = (message) =>
    messageDayLabel(message.createdAt, new Date(), deviceLocale.value || useDates().currentLocale());
const showsDay = (index) => startsNewDay(index > 0 ? visibleMessages.value[index - 1] : null, visibleMessages.value[index]);
const hiddenMessageCount = computed(() => Math.max(0, props.messages.length - renderLimit.value));
const showEarlierMessages = () => {
  renderLimit.value += RENDER_WINDOW;
};
watch(() => props.conversation?.id, () => {
  renderLimit.value = RENDER_WINDOW;
});

// Scroll to bottom of messages
const scrollToBottom = async () => {
  await nextTick();
  // Wait for DOM updates to complete, including auto-grow textarea
  setTimeout(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  }, 50);
};

// Watch for new messages and scroll (length + identity — a deep watch compared the
// entire array of message objects on every update for no extra signal)
watch(() => props.messages.length, async () => {
  await scrollToBottom();
});

// Scroll when conversation changes
watch(() => props.conversation?.id, async () => {
  await scrollToBottom();
}, {immediate: true});

// Handle send message
const handleSend = async (text) => {
  emit('send', text);
  // Scroll will happen automatically via watch
};

// Handle send image message
const handleSendImage = async (imageFile, caption) => {
  emit('send-image', imageFile, caption);
  // Scroll will happen automatically via watch
};

// Handle retry failed message
const handleRetry = (message) => {
  emit('retry', message);
};

// Handle delete failed message
const handleDelete = (message) => {
  emit('delete', message);
};

// Handle typing indicator
const handleTyping = (isTyping) => {
  emit('typing', isTyping);
};

// Handle updating participants
const handleUpdateParticipants = (participantIds) => {
  emit('update-participants', participantIds);
  // Dialog stays open - user can close manually with X button
};

// Handle updating admins
const handleUpdateAdmins = (adminIds) => {
  emit('update-admins', adminIds);
};

// Handle opening edit title dialog
const openEditTitleDialog = () => {
  editFormData.value = {title: props.conversation?.title || ''};
  editFormErrors.value = {};
  showEditTitleDialog.value = true;
};

// Handle updating title
const handleUpdateTitle = async () => {
  editFormPending.value = true;
  editFormErrors.value = {};

  try {
    // Client-side validation
    if (!editFormData.value.title?.trim()) {
      editFormErrors.value.title = ['Group name is required'];
      return;
    }

    // Emit to parent
    emit('update-title', editFormData.value.title.trim());

    // Close dialog
    showEditTitleDialog.value = false;
  } catch (error) {
    console.error('Error updating title:', error);
    editFormErrors.value.global = [error.message || 'Failed to update group name'];
  } finally {
    editFormPending.value = false;
  }
};

// Handle leave conversation
const handleLeaveConversation = () => {
  emit('leave');
};

// Fetch and cache notification status
const fetchNotificationStatus = async () => {
  if (!props.conversation?.id) return;

  try {
    const response = await getConversationNotifications(props.conversation.id);
    cachedNotificationStatus.value = response?.notificationsEnabled ?? true;
  } catch (error) {
    console.error('Error fetching notification status:', error);
    cachedNotificationStatus.value = true; // Default to enabled on error
  }
};

// Handle opening notifications dialog
const openNotificationsDialog = async () => {
  if (!props.conversation?.id) return;

  // Fetch fresh status
  await fetchNotificationStatus();

  // Set form data from cache
  notificationsFormData.value = {
    notificationsEnabled: cachedNotificationStatus.value
  };

  notificationsFormErrors.value = {};
  showNotificationsDialog.value = true;
};

// Handle updating notifications
const handleUpdateNotifications = async () => {
  notificationsFormPending.value = true;
  notificationsFormErrors.value = {};

  try {
    // Emit to parent
    emit('update-notifications', notificationsFormData.value.notificationsEnabled);

    // Update cache with new value
    cachedNotificationStatus.value = notificationsFormData.value.notificationsEnabled;

    // Close dialog
    showNotificationsDialog.value = false;
  } catch (error) {
    console.error('Error updating notifications:', error);
    notificationsFormErrors.value.global = [error.message || 'Failed to update notification preferences'];
  } finally {
    notificationsFormPending.value = false;
  }
};

// Computed for notification options with icons and colors
const notificationOptions = computed(() => [
  {
    value: true,
    title: 'Enabled',
    subtitle: 'You will receive notifications for this conversation',
    icon: 'mdi-bell',
    color: 'success'
  },
  {
    value: false,
    title: 'Disabled',
    subtitle: 'You will not receive notifications for this conversation',
    icon: 'mdi-bell-off',
    color: 'error'
  }
]);

// Watch for conversation changes to fetch notification status
watch(() => props.conversation?.id, async (newId) => {
  if (newId) {
    await fetchNotificationStatus();
  } else {
    cachedNotificationStatus.value = null;
  }
}, { immediate: true });
</script>

<template>
  <div v-if="conversation" class="message-thread">
    <!-- Header (mockup .thread-head) -->
    <div class="thread-head">
      <button type="button" class="th-back" aria-label="Back to conversations" @click="$emit('back')">
        <v-icon size="20">mdi-arrow-left</v-icon>
      </button>

      <avatar-image
          :filename="avatarFilename"
          :fallback="conversationInitials"
          :size="40"
          class="th-face"
      />

      <div class="th-meta">
        <div class="th-name">{{ conversationTitle }}</div>
        <div v-if="showParticipantCount" class="th-sub">{{ participantCount }} {{ participantCount === 1 ? 'person' : 'people' }}</div>
      </div>

      <!-- Conversation menu -->
      <template v-if="showConversationMenu">
        <crud-confirm-dialog
            title="Leave Conversation"
            message="Are you sure you want to leave this conversation? You will need to be re-added by an admin to rejoin."
            confirm-text="Leave"
            @confirm="handleLeaveConversation"
        >
          <template #default="{ click : leaveClick }">
            <v-menu>
              <template #activator="{ props: menuProps }">
                <button type="button" class="th-more" v-bind="menuProps" aria-label="Conversation options">
                  <v-icon size="20">mdi-dots-vertical</v-icon>
                </button>
              </template>

              <v-list density="compact">
                <v-list-item v-if="conversation.conversationType !== 1" @click="showParticipantsDialog = true">
                  <template #prepend>
                    <v-icon size="18">mdi-account-multiple</v-icon>
                  </template>
                  <v-list-item-title>Participants</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="canEditParticipants" @click="openEditTitleDialog">
                  <template #prepend>
                    <v-icon size="18">mdi-pencil</v-icon>
                  </template>
                  <v-list-item-title>Edit Group Name</v-list-item-title>
                </v-list-item>

                <v-list-item @click="openNotificationsDialog">
                  <template #prepend>
                    <v-icon size="18" :color="cachedNotificationStatus !== false ? 'success' : 'error'">
                      {{ cachedNotificationStatus !== false ? 'mdi-bell' : 'mdi-bell-off' }}
                    </v-icon>
                  </template>
                  <v-list-item-title>Notification Preferences</v-list-item-title>
                </v-list-item>

                <!-- No "Report conversation" (owner decision, 25 Sep 2026): a
                     report names one message, from that message's own menu. -->
                <v-list-item v-if="conversation.conversationType === 1 && otherParticipant" :disabled="blockBusy" @click="toggleBlockOther">
                  <template #prepend>
                    <v-icon size="18" :color="otherBlocked ? undefined : 'error'">
                      {{ otherBlocked ? 'mdi-account-check-outline' : 'mdi-account-cancel-outline' }}
                    </v-icon>
                  </template>
                  <v-list-item-title>{{ otherBlocked ? `Unblock ${otherName}` : `Block ${otherName}` }}</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="conversation.conversationType !== 1" @click="leaveClick">
                  <template #prepend>
                    <v-icon color="error" size="18">mdi-exit-to-app</v-icon>
                  </template>
                  <v-list-item-title>Leave Conversation</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </crud-confirm-dialog>
      </template>
    </div>

    <tl-dialog v-model="confirmingBlock" :title="blockCopy.title" :max-width="440">
      <p class="block-confirm-text">{{ blockCopy.body }}</p>
      <div class="block-confirm-actions">
        <v-btn variant="text" :disabled="blockBusy" @click="confirmingBlock = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" :loading="blockBusy" @click="confirmBlockOther">{{ blockCopy.confirm }}</v-btn>
      </div>
    </tl-dialog>

    <!-- Messages list -->
    <div ref="messagesContainer" class="thread-scroll">
      <div v-if="messages.length === 0" class="thread-empty">
        <v-icon size="48" color="textSubtle">mdi-message-outline</v-icon>
        <!-- X223 (R92 O-4): a chat with messages from before you joined (a club chat you joined later) keeps them back; say so -->
        <template v-if="conversation?.earlierMessagesHidden">
          <div class="thread-empty-title">No messages since you joined</div>
          <div class="thread-empty-sub">Messages sent before you joined aren't shown</div>
        </template>
        <template v-else>
          <div class="thread-empty-title">No messages yet</div>
          <div class="thread-empty-sub">Send a message to start the conversation</div>
        </template>
      </div>

      <div v-if="hiddenMessageCount > 0" class="thread-earlier">
        <v-btn variant="text" size="small" prepend-icon="mdi-history" @click="showEarlierMessages">
          Show earlier messages ({{ hiddenMessageCount }})
        </v-btn>
      </div>

      <template v-for="(message, index) in visibleMessages" :key="message.id">
        <div v-if="showsDay(index)" class="thread-day"><span>{{ dayLabelFor(message) }}</span></div>
        <MessageBubble
            :message="message"
            :conversation-id="conversation?.id"
            @retry="handleRetry"
            @delete="handleDelete"
            @delete-for-everyone="emit('delete-for-everyone', $event)"
        />
      </template>
    </div>

    <!-- Typing line (above composer) -->
    <div v-if="typingUsersArray.length > 0" class="thread-typing">
      {{ typingUsersArray.length === 1 ? 'typing…' : `${typingUsersArray.length} people typing…` }}
    </div>

    <!-- Removed from conversation alert -->
    <div v-if="hasLeftConversation" class="thread-left">
      <v-icon size="18" class="mr-2">mdi-account-remove</v-icon>
      <span>You are no longer a participant in this conversation</span>
    </div>

    <!-- Input (only shown if user is still a participant) -->
    <div v-if="isCurrentUserParticipant" class="thread-input">
      <MessageInput
          @send="handleSend"
          @send-image="handleSendImage"
          @typing="handleTyping"
      />
    </div>

    <!-- Participants Dialog -->
    <participants-dialog
        v-model:dialog="showParticipantsDialog"
        :conversation="conversation"
        :can-edit="canEditParticipants"
        @update-participants="handleUpdateParticipants"
        @update-admins="handleUpdateAdmins"
    />

    <!-- Edit Title Dialog -->
    <crud-form-dialog
        :dialog="showEditTitleDialog"
        @update:dialog="showEditTitleDialog = $event"
        v-model="editFormData"
        :form-pending="editFormPending"
        :form-errors="editFormErrors"
        :save-action="handleUpdateTitle"
        action="update"
        update-title="Edit Group Name"
        btn-text="Save"
        btn-icon="mdi-check"
    >
      <template #default="{ item, errors }">
        <v-row>
          <v-col cols="12">
            <div class="tl-label mb-2">Group Name</div>
            <v-text-field
                v-model="item.title"
                placeholder="Enter group name"
                variant="outlined"
                density="comfortable"
                autofocus
                :error="errors.title != null"
                :error-messages="errors.title"
            />
          </v-col>
        </v-row>
      </template>
    </crud-form-dialog>

    <!-- Notification Preferences Dialog -->
    <crud-form-dialog
        :dialog="showNotificationsDialog"
        @update:dialog="showNotificationsDialog = $event"
        v-model="notificationsFormData"
        :form-pending="notificationsFormPending"
        :form-errors="notificationsFormErrors"
        :save-action="handleUpdateNotifications"
        action="update"
        update-title="Notification Preferences"
        btn-text="Save"
        btn-icon="mdi-check"
    >
      <template #default="{ item }">
        <v-radio-group v-model="item.notificationsEnabled" class="mt-0">
          <v-radio
              v-for="option in notificationOptions"
              :key="option.value"
              :value="option.value"
          >
            <template #label>
              <div class="d-flex align-center" style="width: 100%;">
                <v-icon :color="option.color" class="mr-3">{{ option.icon }}</v-icon>
                <div class="flex-grow-1">
                  <div class="font-weight-medium">{{ option.title }}</div>
                  <div class="text-caption text-medium-emphasis">{{ option.subtitle }}</div>
                </div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </template>
    </crud-form-dialog>
  </div>

  <!-- Empty state when no conversation selected -->
  <div v-else class="thread-empty-full">
    <v-icon size="56" color="textSubtle">mdi-message-text-outline</v-icon>
    <div class="thread-empty-title">Select a conversation</div>
    <div class="thread-empty-sub">Choose a conversation from the list to start messaging</div>
  </div>
</template>

<style scoped lang="scss">
.message-thread {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
}

/* header */
.thread-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
  flex-shrink: 0;
}

.th-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  color: rgb(var(--v-theme-text));

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  @media (min-width: 960px) {
    display: none;
  }
}

.th-face :deep(.v-img),
.th-face :deep(.v-avatar) {
  border-radius: 50%;
}

.th-meta {
  flex: 1;
  min-width: 0;
}

.th-name {
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.th-sub {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

.th-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

/* messages */
.thread-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.thread-empty {
  text-align: center;
  color: rgb(var(--v-theme-textSubtle));
  margin: 40px auto;
}

.thread-empty-title {
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
  color: rgb(var(--v-theme-text));
}

.thread-empty-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}

/* typing line above composer */
.thread-typing {
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--v-theme-success));
  padding: 0 18px 8px;
  flex-shrink: 0;
}

/* left-conversation notice */
.thread-left {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  flex-shrink: 0;
}

.thread-input {
  flex-shrink: 0;
}

/* empty state (no conversation selected) */
.thread-empty-full {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgb(var(--v-theme-textSubtle));
}

.thread-scroll::-webkit-scrollbar {
  width: 8px;
}

.thread-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 4px;
}

.thread-day {
  display: flex;
  justify-content: center;
  padding: 4px 0;

  span {
    padding: 3px 12px;
    border-radius: 999px;
    border: 1px solid rgb(var(--v-theme-borderLight));
    background: rgb(var(--v-theme-surface));
    color: rgb(var(--v-theme-textSubtle));
    font-size: 11px;
    font-weight: 600;
  }
}

.thread-earlier {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}
.block-confirm-text { font-size: 14px; line-height: 1.5; max-width: 62ch; margin: 0; }
.block-confirm-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }
</style>
